import { useEffect, useState, useRef } from "react";
import type {
  ReturnsLookupItemV1,
  ReturnsLookupResponseV1,
} from "@made-people/nestjs-bff-service";
import { InitReturnForm } from "./init-return-form";
import { ReturnFlowError } from "./return-flow-error";
import { ReturnItemsSelection } from "./return-items-selection";
import { ReturnStepHeading } from "./return-step-heading";
import { ReturnSuccess } from "./return-success";
import { ShowSummary } from "./show-summary";

type ReturnReasonsTableCell = {
  value: string;
};

export type ReturnReasonsTable = {
  thead: ReturnReasonsTableCell[];
  tbody: { body: ReturnReasonsTableCell[] }[];
};

type ReturnAddressTableCell = {
  _uid: string;
  value: string;
  component: string;
  _editable?: string;
};

type ReturnAddressTableRow = {
  _uid?: string;
  body: ReturnAddressTableCell[];
  component: string;
  _editable?: string;
};

export type ReturnAddressTable = {
  thead: ReturnAddressTableCell[];
  tbody: ReturnAddressTableRow[];
  fieldtype: string;
};

export type ReturnSelections = Record<string, string>;

export type ReturnItemWithLocalSize = ReturnsLookupItemV1 & {
  localSize: string;
};

export type ReturnsLookupResponse = Omit<ReturnsLookupResponseV1, "items"> & {
  items: ReturnItemWithLocalSize[];
};

export type ReturnFlowState =
  | { status: "initState" }
  | {
      status: "itemSelectionState";
      data: ReturnsLookupResponse;
      email: string;
      selections?: ReturnSelections;
    }
  | {
      status: "summaryState";
      data: ReturnsLookupResponse;
      email: string;
      selections: ReturnSelections;
    }
  | {
      status: "successState";
      returnCode: string;
      deliveryCountry: string;
    }
  | { status: "errorState" };

type NonInretrnReturnFlowProps = {
  returnReasons?: ReturnReasonsTable;
  returnDaysLimit: number;
  initialHeader?: string;
  initialSubHeader?: string;
  returnRegistrationHeader?: string;
  returnRegistrationSubHeader?: string;
  returnSummeryHeader?: string;
  returnSummerySubHeader?: string;
  returnCompletedHeader?: string;
  returnCompletedSubHeader?: string;
  returnErrorHeader?: string;
  returnErrorSubHeader?: string;
  returnInstructions?: string;
  customerSupportInformation?: string;
  modalHeader?: string;
  modalSubHeader?: string;
  returnAddress: ReturnAddressTable;
};

export const NonInretrnReturnFlow = ({
  returnReasons,
  returnDaysLimit,
  initialHeader,
  initialSubHeader,
  returnRegistrationHeader,
  returnRegistrationSubHeader,
  returnSummeryHeader,
  returnSummerySubHeader,
  returnCompletedHeader,
  returnCompletedSubHeader,
  returnErrorHeader,
  returnErrorSubHeader,
  returnInstructions,
  customerSupportInformation,
  modalHeader,
  modalSubHeader,
  returnAddress,
}: NonInretrnReturnFlowProps) => {
  const [isReady, setIsReady] = useState(false);
  const [state, setState] = useState<ReturnFlowState>({ status: "initState" });
  const hasMounted = useRef(false);

  useEffect(() => {
    setIsReady(true);
  }, []);

  useEffect(() => {
    if (!isReady) return;

    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    const md = getComputedStyle(document.documentElement)
      .getPropertyValue("--screen-md")
      .trim();

    if (!md || !window.matchMedia(`(width < ${md})`).matches) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    window.scrollTo({
      top: 0,
      behavior: reduceMotion ? "auto" : "smooth",
    });
  }, [state.status, isReady]);

  let step = (
    <InitReturnForm setState={setState} returnDaysLimit={returnDaysLimit} />
  );

  if (!isReady) {
    step = (
      <div className="mt-component-spacing-3xl mb-component-spacing-5xl px-component-spacing-sm">
        <div className="flex min-h-48 items-center justify-center">
          <div
            className="h-10 w-10 animate-spin rounded-full border-2 border-neutral-300 border-t-black"
            aria-label="Loading return form"
            role="status"
          />
        </div>
      </div>
    );
  } else if (state.status === "successState") {
    step = (
      <ReturnSuccess
        returnCode={state.returnCode}
        deliveryCountry={state.deliveryCountry}
        instructions={returnInstructions}
        returnAddress={returnAddress}
      />
    );
  } else if (state.status === "errorState") {
    step = (
      <ReturnFlowError
        customerSupportInformation={customerSupportInformation}
      />
    );
  } else if (state.status === "summaryState") {
    step = (
      <ShowSummary
        data={state.data}
        email={state.email}
        dayLimit={returnDaysLimit}
        returnAddress={returnAddress}
        modalHeader={modalHeader}
        modalSubHeader={modalSubHeader}
        returnReasons={returnReasons}
        selections={state.selections}
        setState={setState}
      />
    );
  } else if (state.status === "itemSelectionState") {
    step = (
      <ReturnItemsSelection
        data={state.data}
        email={state.email}
        returnReasons={returnReasons}
        initialSelections={state.selections}
        setState={setState}
      />
    );
  }

  const stepCopy = {
    initState: {
      header: initialHeader,
      subheader: initialSubHeader,
    },
    itemSelectionState: {
      header: returnRegistrationHeader,
      subheader: returnRegistrationSubHeader,
    },
    summaryState: {
      header: returnSummeryHeader,
      subheader: returnSummerySubHeader,
    },
    successState: {
      header: returnCompletedHeader,
      subheader: returnCompletedSubHeader,
    },
    errorState: {
      header: returnErrorHeader,
      subheader: returnErrorSubHeader,
    },
  }[state.status];

  return (
    <>
      {isReady && (
        <ReturnStepHeading
          header={stepCopy.header}
          subheader={stepCopy.subheader}
        />
      )}
      {step}
    </>
  );
};
